import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Which face of the site is showing.
 *
 *   classic   — the quiet three-column portfolio (the front door)
 *   immersive — the realtime scenes, picked from a menu
 *   windows   — the Windows 11 desktop
 *
 * The URL is the only thing that decides this, mirrored as ?mode=… so any
 * face can be linked to directly. Deliberately *not* remembered between
 * visits: a bare / is always the classic portfolio, because someone arriving
 * at the root — a recruiter following a link, most of the time — should get
 * the readable version rather than whichever face this browser happened to
 * wander into last month. Switching face rewrites the URL, so a refresh still
 * keeps you where you are.
 *
 * The chosen scene *is* remembered, since that is a preference within the
 * immersive face rather than a choice about which site you are looking at.
 */

const SCENE_KEY = "immersive-scene";
const MODES = ["immersive", "classic", "windows"];
const DEFAULT_MODE = "classic";
// Kept as literals rather than imported from src/immersive so that choosing a
// scene never pulls the (lazy, three.js-heavy) immersive bundle into the shell.
const SCENES = ["particles", "monolith", "ink", "gallery"];
const DEFAULT_SCENE = "particles";

const ViewModeContext = createContext(null);

export const useViewMode = () => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) throw new Error("useViewMode must be used inside <ViewModeProvider>");
  return ctx;
};

const readParam = (param, allowed, fallback) => {
  if (typeof window === "undefined") return fallback;
  const fromUrl = new URLSearchParams(window.location.search).get(param);
  return allowed.includes(fromUrl) ? fromUrl : fallback;
};

const readInitialMode = () => readParam("mode", MODES, DEFAULT_MODE);

const readInitialScene = () => {
  const fromUrl = readParam("scene", SCENES, null);
  if (fromUrl) return fromUrl;
  try {
    const stored = localStorage.getItem(SCENE_KEY);
    return SCENES.includes(stored) ? stored : DEFAULT_SCENE;
  } catch {
    return DEFAULT_SCENE;
  }
};

export function ViewModeProvider({ children }) {
  const [mode, setMode] = useState(readInitialMode);
  const [scene, setScene] = useState(readInitialScene);

  useEffect(() => {
    try {
      localStorage.setItem(SCENE_KEY, scene);
    } catch {
      /* private mode — the choice just won't stick */
    }

    // Keep the URL shareable without needing a server-side route.
    const url = new URL(window.location.href);
    if (mode === DEFAULT_MODE) url.searchParams.delete("mode");
    else url.searchParams.set("mode", mode);
    if (mode === "immersive" && scene !== DEFAULT_SCENE) {
      url.searchParams.set("scene", scene);
    } else {
      url.searchParams.delete("scene");
    }
    window.history.replaceState({}, "", url);
  }, [mode, scene]);

  // Switching faces should land at the top of the new one. Kept apart from the
  // effect above so that changing scene mid-scroll doesn't yank the page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      scene,
      isImmersive: mode === "immersive",
      isWindows: mode === "windows",
      isClassic: mode === "classic",
      setMode: (next) => setMode(MODES.includes(next) ? next : DEFAULT_MODE),
      setScene: (next) => setScene(SCENES.includes(next) ? next : DEFAULT_SCENE),
      enterImmersive: (nextScene) => {
        if (nextScene && SCENES.includes(nextScene)) setScene(nextScene);
        setMode("immersive");
      },
      enterWindows: () => setMode("windows"),
      exitWindows: () => setMode("classic"),
      exitImmersive: () => setMode("classic"),
    }),
    [mode, scene]
  );

  return (
    <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>
  );
}
